import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { toast } from "sonner";
import { MessageCircle, Send, Trash2 } from "lucide-react";

const fillTemplate = (text, r) => {
  const name = r?.contact_name || "";
  return text.replace(/\{vorname\}/g, name.split(" ")[0] || "").replace(/\{name\}/g, name).replace(/\{route\}/g, r?.route_label || "")
    .replace(/\{volumen\}/g, r?.volume_m3 ? `${r.volume_m3} m³` : "m³").replace(/\s+,/g, ",");
};

export const RequestChat = ({ requestId, token, me = "customer", defaultOpen = false, request = null }) => {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { if (me === "admin") api.get("/settings").then((r) => setTemplates(r.data.reply_templates || [])).catch(() => {}); }, [me]);
  const [open, setOpen] = useState(defaultOpen);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const params = token ? { token } : {};

  const load = async () => { try { const { data } = await api.get(`/requests/${requestId}/messages`, { params }); setMsgs(data); } catch {} };
  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [open, requestId]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/requests/${requestId}/messages`, { text }, { params }); setMsgs((m) => [...m, data]); setText(""); toast.success(me === "admin" ? "Antwort gesendet – Kunde wird per E-Mail informiert" : "Nachricht gesendet"); window.dispatchEvent(new Event("request-message-sent")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const delMsg = async (m) => {
    if (!window.confirm(`Nachricht von ${m.sender_role === "admin" ? "Easy Beiladung" : m.sender_name} löschen?\n„${m.text.slice(0, 80)}“`)) return;
    try { await api.delete(`/admin/requests/${requestId}/messages/${m.id}`); setMsgs((ms) => ms.filter((x) => x.id !== m.id)); toast.success("Nachricht gelöscht"); window.dispatchEvent(new Event("request-message-sent")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delThread = async () => {
    if (window.prompt(`Gesamten Nachrichten-Thread (${msgs.length} Nachrichten) dieser Anfrage löschen?\nZur Bestätigung LOESCHEN eingeben:`) !== "LOESCHEN") return;
    try { await api.delete(`/admin/requests/${requestId}/messages`, { params: { confirm: "LOESCHEN" } }); setMsgs([]); toast.success("Thread gelöscht"); window.dispatchEvent(new Event("request-message-sent")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };


  return (
    <div className="mt-4 border-t border-slate-800 pt-3" data-testid={`request-chat-${requestId}`}>
      <button onClick={() => setOpen(!open)} className="eb-btn-outline !py-1.5 text-xs" data-testid={`request-chat-toggle-${requestId}`}>
        <MessageCircle className="w-3.5 h-3.5" /> {open ? "Nachrichten schließen" : me === "admin" ? "Kunden antworten" : "Rückfrage an Easy Beiladung"}
      </button>
      {open && (
        <div className="mt-3 rounded-lg border border-slate-800 bg-[#0B0F17]">
          <div className="max-h-64 overflow-y-auto p-3 space-y-2" data-testid={`request-chat-messages-${requestId}`}>
            {msgs.length === 0 && <p className="text-xs text-slate-500">Noch keine Nachrichten zu dieser Anfrage.</p>}
            {msgs.map((m) => (
              <div key={m.id} className={`flex items-start gap-1 ${m.sender_role === me ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.sender_role === me ? "bg-[#FF5A1F]/20 text-white" : "bg-slate-800 text-slate-200"}`} data-testid={`request-chat-msg-${m.id}`}>
                  <div className="text-[10px] text-slate-400 mb-0.5">{m.sender_role === "admin" ? "Easy Beiladung" : m.sender_name} · {m.created_at?.slice(11, 16)} · {fmtDate(m.created_at)}</div>
                  {m.text}
                </div>
                {me === "admin" && <button onClick={() => delMsg(m)} className="text-slate-600 hover:text-red-400 mt-1" title="Nachricht löschen" data-testid={`request-chat-del-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div className="flex gap-2 p-2 border-t border-slate-800">
            {me === "admin" ? (
              <textarea className="eb-input flex-1 !py-2 text-sm min-h-[64px]" placeholder="Antwort an den Kunden…" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} data-testid={`request-chat-input-${requestId}`} />
            ) : (
              <input className="eb-input flex-1 !py-2 text-sm" placeholder="Deine Nachricht…" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()} data-testid={`request-chat-input-${requestId}`} />
            )}
            <button onClick={send} disabled={busy || !text.trim()} className="eb-btn-primary !py-2 !px-3" data-testid={`request-chat-send-${requestId}`}><Send className="w-4 h-4" /></button>
          </div>
          {me === "admin" && (
            <div className="flex flex-wrap gap-1.5 px-2 pb-2" data-testid={`request-chat-templates-${requestId}`}>
              <span className="text-[11px] text-slate-500 self-center">Schnellantwort:</span>
              {templates.map((t) => (
                <button key={t.label} type="button" onClick={() => setText(fillTemplate(t.text, request))} className="eb-btn-ghost !py-1 !px-2 text-[11px]" data-testid={`request-chat-template-${t.label.replace(/\s/g, "-").toLowerCase()}-${requestId}`}>{t.label}</button>
              ))}
              {msgs.length > 0 && <button type="button" onClick={delThread} className="eb-btn-ghost !py-1 !px-2 text-[11px] text-red-400 ml-auto" data-testid={`request-chat-del-thread-${requestId}`}><Trash2 className="w-3 h-3" /> Thread löschen</button>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
